import React from "react";
import { cn } from "../../../lib/utils";
import { Button, Input } from "../../../shared/ui/composites";
import { fmtInt } from "../../../shared/utils/formatters";
import { emptyGroupPercents, sumNumbers, almostEqual } from "../utils/commissionConfig";
import { calendarTypeLabel, normalizeCalendarType } from "../../../shared/utils/calendarType";

function PercentCell({ disabled, value, onChange, placeholder = "0" }) {
  if (disabled) {
    return <span className="block text-center text-slate-300">-</span>;
  }
  return (
    <label className="flex items-center justify-end gap-1">
      <Input
        type="number"
        min="0"
        step="0.01"
        inputMode="decimal"
        variant="form"
        className="h-9 w-[4.5rem] px-2 text-left"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <span className="text-xs text-slate-400">%</span>
    </label>
  );
}

export default function StaffProductGroupsTab({
  targetGroups,
  selectedIds,
  percents,
  onToggle,
  onToggleAll,
  onPercentChange,
}) {
  const selected = new Set(selectedIds);
  const allSelected = targetGroups.length > 0 && selectedIds.length === targetGroups.length;
  const basketSum = sumNumbers(
    selectedIds.map((id) => percents[id]?.basket_percent),
  );
  const basketOk = selectedIds.length === 0 || almostEqual(basketSum, 100);

  return (
    <div className="py-2 min-h-56">
      <div className="mb-3 flex items-center gap-3">
        <Button
          type="button"
          variant="ghost"
          className={cn(
            "px-3 py-1.5 bg-green-100 text-green-800 hover:bg-green-200",
            allSelected && "bg-red-100 text-red-800 hover:bg-red-300",
          )}
          onClick={onToggleAll}
        >
          {allSelected ? "✕ حذف همه" : "✓ انتخاب همه"}
        </Button>
        <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
          ✓ {fmtInt(selectedIds.length)} از {fmtInt(targetGroups.length)} انتخاب شده
        </span>
      </div>

      <div className="max-h-[320px] overflow-auto rounded-xl border border-border">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-slate-50 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-right font-semibold">گروه کالایی</th>
              <th className="w-32 px-2 py-2 text-center font-semibold">درصد سبد</th>
              <th className="w-36 px-2 py-2 text-center font-semibold">
                درصد مازاد
                <div className="text-[10px] font-normal text-slate-400">
                  اختیاری؛ بر اساس فروش ریالی سبد
                </div>
              </th>
              <th className="w-36 px-2 py-2 text-center font-semibold">
                سقف درصد اعمالی
                <div className="text-[10px] font-normal text-slate-400">
                  اختیاری؛ سقف ریالی ۲٪ فروش در فرمول است
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {targetGroups.map((p) => {
              const checked = selected.has(p.id);
              const row = percents[p.id] || emptyGroupPercents();
              return (
                <tr key={p.id} className={cn("border-t border-slate-100", checked && "bg-indigo-50/40")}>
                  <td className="px-3 py-2">
                    <label className="flex cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => onToggle(p.id)}
                      />
                      <span className="font-medium text-slate-800">
                        {p.name} {p.unit ? `(${p.unit})` : ""}
                        <span
                          className={
                            normalizeCalendarType(p.calendar_type) === "gregorian"
                              ? "mr-1 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold text-amber-700"
                              : "mr-1 rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-bold text-indigo-700"
                          }
                        >
                          {calendarTypeLabel(p.calendar_type)}
                        </span>
                      </span>
                    </label>
                  </td>
                  <td className="px-2 py-1.5">
                    <PercentCell
                      disabled={!checked}
                      value={row.basket_percent}
                      onChange={(v) => onPercentChange(p.id, "basket_percent", v)}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <PercentCell
                      disabled={!checked}
                      value={row.excess_percent}
                      onChange={(v) => onPercentChange(p.id, "excess_percent", v)}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <PercentCell
                      disabled={!checked}
                      value={row.max_payout_percent}
                      onChange={(v) => onPercentChange(p.id, "max_payout_percent", v)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div
        className={cn(
          "mt-3 rounded-xl px-3 py-2 text-sm font-semibold",
          basketOk
            ? "bg-sky-50 text-sky-800"
            : "bg-amber-50 text-amber-800",
        )}
      >
        مجموع درصد سبدها: {basketSum.toFixed(2)}%
        {selectedIds.length > 0 && !basketOk ? " — باید برابر ۱۰۰٪ باشد" : ""}
      </div>
    </div>
  );
}
